import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { ListaOrdenavel } from "../../../components/ListaOrdenavel";
import { aplicarOrdem } from "../../../hooks/useOrdenavel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  WiredConexaoModal,
  WiredEstatisticaModal,
  WiredFuncionalidadeModal,
  WiredHudAcaoModal,
  WiredHudVariavelModal,
  WiredLojaConfigModal,
  WiredLojaItemModal,
  WiredMissaoModal,
  WiredNivelModal,
  WiredTesteConexaoModal,
} from "../../../components/modals";
import {
  Plus,
  Edit,
  Trash2,
  Plug,
  Variable,
  CheckCircle,
  XCircle,
  KeyRound,
  Loader2,
  Activity,
  Gamepad2,
  ScrollText,
  LayoutDashboard,
  MousePointerClick,
  ShoppingBag,
  Settings2,
  Award,
  Swords,
  BarChart3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePermissions, PERMISSOES } from "../../../hooks/usePermissions";
import {
  WIRED_HUD_FORMATOS,
  WIRED_HUD_VISOES,
  WIRED_LOJA_GRUPOS,
  WiredConexao,
  WiredEstatistica,
  WiredFuncionalidade,
  WiredHudAcao,
  WiredHudVariavel,
  WiredLojaConfig,
  WiredLojaItem,
  WiredMissao,
  WiredMissaoRegistroAdmin,
  WiredNivel,
  WiredTesteConexaoResposta,
  wiredService,
} from "../../../services/wired";

const greenOutlineButton =
  "text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20";

const activeBadge = "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700";
const inactiveBadge = "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600";

function AtivoBadge({ ativo }: { ativo: boolean }) {
  return (
    <Badge className={ativo ? activeBadge : inactiveBadge}>
      {ativo ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
      {ativo ? "Ativo" : "Inativo"}
    </Badge>
  );
}

export function WiredConfiguracoes() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const podeGerenciar = hasPermission(PERMISSOES.GERENCIAR_WIRED_CONFIGURACOES);
  const podeVerLogs = hasPermission(PERMISSOES.VISUALIZAR_WIRED_LOGS);
  const podeGerenciarHud = hasPermission(PERMISSOES.GERENCIAR_WIRED_HUD);

  const [conexoes, setConexoes] = useState<WiredConexao[]>([]);
  const [funcionalidades, setFuncionalidades] = useState<WiredFuncionalidade[]>([]);
  const [hudVariaveis, setHudVariaveis] = useState<WiredHudVariavel[]>([]);
  const [hudAcoes, setHudAcoes] = useState<WiredHudAcao[]>([]);
  const [lojaConfigs, setLojaConfigs] = useState<WiredLojaConfig[]>([]);
  const [lojaItens, setLojaItens] = useState<WiredLojaItem[]>([]);
  const [isLojaConfigOpen, setIsLojaConfigOpen] = useState(false);
  const [editingLojaConfig, setEditingLojaConfig] = useState<WiredLojaConfig | null>(null);
  const [isLojaItemOpen, setIsLojaItemOpen] = useState(false);
  const [editingLojaItem, setEditingLojaItem] = useState<WiredLojaItem | null>(null);
  const [niveis, setNiveis] = useState<WiredNivel[]>([]);
  const [estatisticas, setEstatisticas] = useState<WiredEstatistica[]>([]);
  const [isEstatisticaOpen, setIsEstatisticaOpen] = useState(false);
  const [editingEstatistica, setEditingEstatistica] = useState<WiredEstatistica | null>(null);
  const [missoes, setMissoes] = useState<WiredMissao[]>([]);
  const [isMissaoOpen, setIsMissaoOpen] = useState(false);
  const [editingMissao, setEditingMissao] = useState<WiredMissao | null>(null);
  const [limiteGlobal, setLimiteGlobal] = useState("");
  const [registrosAbertos, setRegistrosAbertos] = useState<string | null>(null);
  const [registros, setRegistros] = useState<WiredMissaoRegistroAdmin[] | null>(null);
  const [isNivelOpen, setIsNivelOpen] = useState(false);
  const [editingNivel, setEditingNivel] = useState<WiredNivel | null>(null);
  const [loading, setLoading] = useState(false);
  const [aba, setAba] = useState<"conexoes" | "funcionalidades" | "hud" | "loja" | "niveis" | "missoes" | "estatisticas">("conexoes");

  const [isHudModalOpen, setIsHudModalOpen] = useState(false);
  const [editingHud, setEditingHud] = useState<WiredHudVariavel | null>(null);
  const [isAcaoModalOpen, setIsAcaoModalOpen] = useState(false);
  const [editingAcao, setEditingAcao] = useState<WiredHudAcao | null>(null);
  const [isConexaoModalOpen, setIsConexaoModalOpen] = useState(false);
  const [editingConexao, setEditingConexao] = useState<WiredConexao | null>(null);
  const [editingFuncionalidade, setEditingFuncionalidade] = useState<WiredFuncionalidade | null>(null);

  const [testingId, setTestingId] = useState<string | null>(null);
  const [testeConexao, setTesteConexao] = useState<WiredConexao | null>(null);
  const [testeResultado, setTesteResultado] = useState<WiredTesteConexaoResposta | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [conexoesData, funcionalidadesData, hudData, acoesData, lojaCfgData, lojaItensData, niveisData, missoesData, missoesCfg, estatisticasData] = await Promise.all([
        wiredService.listarConexoes(),
        wiredService.listarFuncionalidades(),
        podeGerenciarHud ? wiredService.listarHudVariaveis() : Promise.resolve([] as WiredHudVariavel[]),
        podeGerenciarHud ? wiredService.listarHudAcoes() : Promise.resolve([] as WiredHudAcao[]),
        wiredService.listarLojaConfig(),
        wiredService.listarLojaItens(),
        wiredService.listarNiveis(),
        wiredService.listarMissoes(),
        wiredService.obterConfigMissoes(),
        wiredService.listarEstatisticas(),
      ]);
      setEstatisticas(estatisticasData);
      setMissoes(missoesData);
      setLimiteGlobal(missoesCfg.limite_simultaneas != null ? String(missoesCfg.limite_simultaneas) : "");
      setNiveis(niveisData);
      setConexoes(conexoesData);
      setFuncionalidades(funcionalidadesData);
      setHudVariaveis(hudData);
      setHudAcoes(acoesData);
      setLojaConfigs(lojaCfgData);
      setLojaItens(lojaItensData);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar configurações do Wired", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [podeGerenciarHud]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const reordenacao = <T extends { id: string; ordem: number }>(
    recurso: Parameters<typeof wiredService.reordenar>[0],
    setLista: Dispatch<SetStateAction<T[]>>
  ) => ({
    onMover: (ids: string[]) => setLista((lista) => aplicarOrdem(lista, ids)),
    onSalvar: async (ids: string[]) => {
      try {
        await wiredService.reordenar(recurso, ids);
      } catch (error: any) {
        enqueueSnackbar(error.response?.data?.message || "Erro ao salvar a nova ordem", { variant: "error" });
        loadData();
      }
    },
  });

  const handleNovaConexao = () => {
    setEditingConexao(null);
    setIsConexaoModalOpen(true);
  };

  const handleEditConexao = (conexao: WiredConexao) => {
    setEditingConexao(conexao);
    setIsConexaoModalOpen(true);
  };

  const handleCloseConexaoModal = () => {
    setIsConexaoModalOpen(false);
    setEditingConexao(null);
  };

  const handleDeleteConexao = async (conexao: WiredConexao) => {
    if (!window.confirm(`Deseja excluir a conexão "${conexao.nome}"?`)) return;

    try {
      await wiredService.excluirConexao(conexao.id);
      enqueueSnackbar("Conexão excluída com sucesso!", { variant: "success" });
      loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir conexão", { variant: "error" });
    }
  };

  const handleTestarConexao = async (conexao: WiredConexao) => {
    try {
      setTestingId(conexao.id);
      const resultado = await wiredService.testarConexao(conexao.id);
      setTesteConexao(conexao);
      setTesteResultado(resultado);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao testar conexão", { variant: "error" });
    } finally {
      setTestingId(null);
    }
  };

  const handleCloseTeste = () => {
    setTesteConexao(null);
    setTesteResultado(null);
  };

  const handleNovaHud = () => {
    setEditingHud(null);
    setIsHudModalOpen(true);
  };

  const handleEditHud = (variavel: WiredHudVariavel) => {
    setEditingHud(variavel);
    setIsHudModalOpen(true);
  };

  const handleCloseHudModal = () => {
    setIsHudModalOpen(false);
    setEditingHud(null);
  };

  const handleDeleteHud = async (variavel: WiredHudVariavel) => {
    if (!window.confirm(`Deseja remover "${variavel.rotulo}" do HUD?`)) return;

    try {
      await wiredService.excluirHudVariavel(variavel.id);
      enqueueSnackbar("Variável removida do HUD!", { variant: "success" });
      loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao remover variável do HUD", { variant: "error" });
    }
  };

  const handleNovaAcao = () => {
    setEditingAcao(null);
    setIsAcaoModalOpen(true);
  };

  const handleEditAcao = (acao: WiredHudAcao) => {
    setEditingAcao(acao);
    setIsAcaoModalOpen(true);
  };

  const handleCloseAcaoModal = () => {
    setIsAcaoModalOpen(false);
    setEditingAcao(null);
  };

  const handleDeleteAcao = async (acao: WiredHudAcao) => {
    const vinculados = hudVariaveis.filter((v) => v.id_acao === acao.id).length;
    const aviso = vinculados > 0 ? ` ${vinculados} cartão(ões) do HUD ficarão sem botão.` : "";
    if (!window.confirm(`Deseja excluir a ação "${acao.rotulo}"?${aviso}`)) return;

    try {
      await wiredService.excluirHudAcao(acao.id);
      enqueueSnackbar("Ação excluída!", { variant: "success" });
      loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir ação", { variant: "error" });
    }
  };

  const limiteDaBarra = (item: WiredHudVariavel) =>
    item.max_nome_variavel ? `limite: ${item.max_nome_variavel}` : `limite fixo: ${item.max_valor_fixo}`;

  const handleNovoLojaItem = () => {
    setEditingLojaItem(null);
    setIsLojaItemOpen(true);
  };

  const handleEditLojaItem = (item: WiredLojaItem) => {
    setEditingLojaItem(item);
    setIsLojaItemOpen(true);
  };

  const handleDeleteLojaItem = async (item: WiredLojaItem) => {
    if (!window.confirm(`Deseja excluir o item "${item.rotulo}"? O histórico de compras dele também será removido.`)) {
      return;
    }
    try {
      await wiredService.excluirLojaItem(item.id);
      enqueueSnackbar("Item excluído!", { variant: "success" });
      loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir item", { variant: "error" });
    }
  };

  const handleEditLojaConfig = (config: WiredLojaConfig | null) => {
    setEditingLojaConfig(config);
    setIsLojaConfigOpen(true);
  };

  const handleDeleteNivel = async (n: WiredNivel) => {
    if (!window.confirm(`Deseja excluir o sistema de nível "${n.nome}"? O histórico de recompensas também será removido.`)) {
      return;
    }
    try {
      await wiredService.excluirNivel(n.id);
      enqueueSnackbar("Sistema de nível excluído!", { variant: "success" });
      loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir sistema de nível", { variant: "error" });
    }
  };

  const handleDeleteEstatistica = async (e: WiredEstatistica) => {
    if (!window.confirm(`Deseja excluir a estatística "${e.rotulo}"?`)) return;
    try {
      await wiredService.excluirEstatistica(e.id);
      enqueueSnackbar("Estatística excluída!", { variant: "success" });
      loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir estatística", { variant: "error" });
    }
  };

  const handleDeleteMissao = async (m: WiredMissao) => {
    if (!window.confirm(`Deseja excluir a missão "${m.nome}"? Os registros dos militares também serão removidos.`)) {
      return;
    }
    try {
      await wiredService.excluirMissao(m.id);
      enqueueSnackbar("Missão excluída!", { variant: "success" });
      loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir missão", { variant: "error" });
    }
  };

  const handleSalvarLimiteGlobal = async () => {
    const texto = limiteGlobal.trim();
    const n = Number(texto);
    if (texto && (!Number.isInteger(n) || n < 1 || n > 100)) {
      enqueueSnackbar("O limite deve ser um inteiro entre 1 e 100.", { variant: "error" });
      return;
    }
    try {
      await wiredService.salvarConfigMissoes(texto ? n : null);
      enqueueSnackbar("Limite salvo!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar limite", { variant: "error" });
    }
  };

  const handleToggleRegistros = async (id: string) => {
    if (registrosAbertos === id) {
      setRegistrosAbertos(null);
      return;
    }
    setRegistrosAbertos(id);
    setRegistros(null);
    try {
      setRegistros(await wiredService.listarRegistrosMissao(id));
    } catch (error: any) {
      setRegistros([]);
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar registros", { variant: "error" });
    }
  };

  const abas: { id: typeof aba; label: string }[] = [
    { id: "conexoes", label: "Conexões" },
    { id: "funcionalidades", label: "Funcionalidades" },
    ...(podeGerenciarHud ? [{ id: "hud" as const, label: "HUD" }] : []),
    { id: "loja", label: "Loja de atributos" },
    { id: "niveis", label: "Níveis" },
    { id: "missoes", label: "Missões" },
    { id: "estatisticas", label: "Estatísticas" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Gamepad2 className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
            Habbo Wired
          </h1>
          <p className="text-muted-foreground mt-2">
            Configure as conexões com os quartos do Habbo e as variáveis WIRED sincronizadas
          </p>
        </div>
        {podeVerLogs && (
          <Button
            variant="outline"
            onClick={() => navigate("/configuracoes/wired/logs")}
            className={greenOutlineButton}
          >
            <ScrollText className="w-4 h-4 mr-2" />
            Ver Logs
          </Button>
        )}
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {abas.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => setAba(a.id)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              aba === a.id
                ? "border-[oklch(0.45_0.15_142)] text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {a.label}
          </button>
        ))}
      </div>

      {/* Conexões */}
      {aba === "conexoes" && (
      <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
              <Plug className="w-5 h-5" />
              Conexões
            </CardTitle>
            <CardDescription>
              {conexoes.length} conexão(ões) cadastrada(s). As chaves nunca são exibidas por completo.
            </CardDescription>
          </div>
          {podeGerenciar && (
            <Button onClick={handleNovaConexao} className="bg-green-600 hover:bg-green-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Nova Conexão
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading && conexoes.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Carregando...</div>
            </div>
          ) : conexoes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Nenhuma conexão cadastrada</div>
          ) : (
            <div className="space-y-4">
              {conexoes.map((conexao) => (
                <div
                  key={conexao.id}
                  className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center shrink-0">
                      <Plug className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{conexao.nome}</h3>
                        <AtivoBadge ativo={conexao.ativo} />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Quarto: <span className="font-mono font-medium">{conexao.room_id}</span>
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                        <KeyRound className="h-3 w-3" />
                        Leitura: <span className="font-mono">{conexao.chave_leitura_mascarada}</span>
                        <span className="mx-1">•</span>
                        Escrita:{" "}
                        {conexao.possui_chave_escrita ? (
                          <span className="font-mono">{conexao.chave_escrita_mascarada || "configurada"}</span>
                        ) : (
                          <span>não configurada</span>
                        )}
                      </p>
                      <div className="flex items-center gap-1 flex-wrap pt-1">
                        {conexao.funcionalidades.length === 0 ? (
                          <span className="text-xs text-muted-foreground">Nenhuma funcionalidade vinculada</span>
                        ) : (
                          conexao.funcionalidades.map((codigo) => (
                            <Badge
                              key={codigo}
                              className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700 font-mono"
                            >
                              {codigo}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {podeGerenciar && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestarConexao(conexao)}
                        disabled={testingId === conexao.id}
                        className={greenOutlineButton}
                      >
                        {testingId === conexao.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Activity className="w-4 h-4 mr-2" />
                        )}
                        Testar conexão
                      </Button>
                    )}
                    {podeGerenciar && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditConexao(conexao)}
                        className={greenOutlineButton}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    )}
                    {podeGerenciar && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteConexao(conexao)}
                        className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950/40"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {aba === "funcionalidades" && (
      <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
            <Variable className="w-5 h-5" />
            Funcionalidades
          </CardTitle>
          <CardDescription>
            Vincule cada funcionalidade a uma conexão e à variável WIRED que receberá o valor
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && funcionalidades.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Carregando...</div>
            </div>
          ) : funcionalidades.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Nenhuma funcionalidade encontrada</div>
          ) : (
            <div className="space-y-4">
              {funcionalidades.map((func) => (
                <div
                  key={func.id}
                  className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center shrink-0">
                      <Variable className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{func.descricao}</h3>
                        <AtivoBadge ativo={func.ativo} />
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">{func.codigo}</p>
                      <p className="text-sm text-muted-foreground">
                        Conexão:{" "}
                        {func.conexao_nome ? (
                          <span className="font-medium text-foreground">{func.conexao_nome}</span>
                        ) : (
                          <span className="italic">nenhuma</span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Variável:{" "}
                        {func.nome_variavel ? (
                          <span className="font-mono font-medium text-foreground">{func.nome_variavel}</span>
                        ) : (
                          <span className="italic">não definida</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {podeGerenciar && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingFuncionalidade(func)}
                        className={greenOutlineButton}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {aba === "hud" && podeGerenciarHud && (
      <>
      {podeGerenciarHud && (
        <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
                <LayoutDashboard className="w-5 h-5" />
                HUD
              </CardTitle>
              <CardDescription>
                Variáveis do jogo exibidas no app HUD (janela flutuante) dos militares
              </CardDescription>
            </div>
            <Button
              onClick={handleNovaHud}
              disabled={conexoes.length === 0}
              className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova variável
            </Button>
          </CardHeader>
          <CardContent>
            {loading && hudVariaveis.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-muted-foreground">Carregando...</div>
              </div>
            ) : hudVariaveis.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {conexoes.length === 0
                  ? "Cadastre uma conexão antes de adicionar variáveis ao HUD"
                  : "Nenhuma variável cadastrada no HUD"}
              </div>
            ) : (
              <ListaOrdenavel itens={hudVariaveis} {...reordenacao("hud/variaveis", setHudVariaveis)}>
                {(item) => (
                  <div
                    className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{item.rotulo}</h3>
                        <AtivoBadge ativo={item.ativo} />
                        <Badge variant="outline">
                          {WIRED_HUD_VISOES.find((v) => v.value === item.visao)?.label.split(" (")[0] ?? item.visao}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">{item.nome_variavel}</p>
                      <p className="text-sm text-muted-foreground">
                        Conexão: <span className="font-medium text-foreground">{item.conexao_nome ?? "-"}</span>
                        {" · "}Formato:{" "}
                        <span className="font-medium text-foreground">
                          {WIRED_HUD_FORMATOS.find((f) => f.value === item.formato)?.label ?? item.formato}
                        </span>
                        {" · "}Ordem: <span className="font-medium text-foreground">{item.ordem}</span>
                      </p>
                      {item.formato === "barra" && (
                        <p className="text-sm text-muted-foreground">
                          Barra <span className="font-mono text-foreground">({limiteDaBarra(item)})</span>
                        </p>
                      )}
                      {item.id_acao && (
                        <p className="text-sm text-muted-foreground">
                          Botão:{" "}
                          <span className="font-medium text-foreground">
                            {hudAcoes.find((a) => a.id === item.id_acao)?.rotulo ?? "ação removida"}
                          </span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditHud(item)}
                        className={greenOutlineButton}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteHud(item)}
                        className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </ListaOrdenavel>
            )}
          </CardContent>
        </Card>
      )}

      {podeGerenciarHud && (
        <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
                <MousePointerClick className="w-5 h-5" />
                Ações do HUD
              </CardTitle>
              <CardDescription>
                Botões do HUD que gravam um valor numa variável do jogo (ex.: usar um item do inventário)
              </CardDescription>
            </div>
            <Button
              onClick={handleNovaAcao}
              disabled={conexoes.length === 0}
              className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova ação
            </Button>
          </CardHeader>
          <CardContent>
            {hudAcoes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Nenhuma ação cadastrada</div>
            ) : (
              <ListaOrdenavel itens={hudAcoes} {...reordenacao("hud/acoes", setHudAcoes)}>
                {(acao) => (
                  <div
                    className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{acao.rotulo}</h3>
                        <AtivoBadge ativo={acao.ativo} />
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">
                        {acao.nome_variavel_disparo} = {acao.valor_disparo}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Conexão: <span className="font-medium text-foreground">{acao.conexao_nome ?? "-"}</span>
                        {" · "}Requisito:{" "}
                        <span className="font-mono font-medium text-foreground">
                          {acao.nome_variavel_requisito
                            ? `${acao.nome_variavel_requisito} ≥ ${acao.requisito_minimo}`
                            : "nenhum"}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditAcao(acao)}
                        className={greenOutlineButton}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAcao(acao)}
                        className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </ListaOrdenavel>
            )}
          </CardContent>
        </Card>
      )}
      </>
      )}

      {aba === "loja" && (
      <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
              <ShoppingBag className="w-5 h-5" />
              Loja de atributos
            </CardTitle>
            <CardDescription>
              Itens que os militares compram com pontos de atributo (aumentam o máximo de status e inventário)
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              variant="outline"
              onClick={() => handleEditLojaConfig(lojaConfigs[0] ?? null)}
              disabled={conexoes.length === 0}
              className={greenOutlineButton}
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Variáveis da loja
            </Button>
            <Button
              onClick={handleNovoLojaItem}
              disabled={conexoes.length === 0 || lojaConfigs.length === 0}
              className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {lojaConfigs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Defina as variáveis da loja (pontos de atributo e elegibilidade) para começar
            </div>
          ) : (
            <>
              {lojaConfigs.map((cfg) => (
                <p key={cfg.id} className="text-sm text-muted-foreground">
                  Pontos: <span className="font-mono font-medium text-foreground">{cfg.nome_variavel_pontos}</span>
                  {" · "}Acesso ao Sobrevivente:{" "}
                  <span className="font-mono font-medium text-foreground">
                    {cfg.nome_variavel_elegibilidade ?? "sem restrição"}
                  </span>
                  {" · "}Conexão: <span className="font-medium text-foreground">{cfg.conexao_nome ?? "-"}</span>{" "}
                  <AtivoBadge ativo={cfg.ativo} />
                </p>
              ))}
              {lojaItens.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Nenhum item cadastrado</div>
              ) : (
                WIRED_LOJA_GRUPOS.map((grupo) => {
                  const itens = lojaItens.filter((i) => i.grupo === grupo.value);
                  if (itens.length === 0) return null;
                  return (
                    <div key={grupo.value} className="space-y-3">
                      <h4 className="text-sm font-semibold text-foreground">{grupo.label}</h4>
                      <ListaOrdenavel
                        itens={itens}
                        className="space-y-3"
                        {...reordenacao("loja/itens", setLojaItens)}
                      >
                        {(item) => (
                        <div
                          className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-foreground">{item.rotulo}</h3>
                              <AtivoBadge ativo={item.ativo} />
                            </div>
                            <p className="text-xs text-muted-foreground font-mono">
                              {item.nome_variavel_alvo} {item.modo === "incremento" ? `+= ${item.valor}` : `= ${item.valor}`}
                              {item.teto_valor ? ` (teto ${item.teto_valor})` : ""}
                            </p>
                            {item.efeitos_extras.map((e) => (
                              <p key={e.nome_variavel} className="text-xs text-muted-foreground font-mono">
                                {e.nome_variavel} {e.modo === "incremento" ? `+= ${e.valor}` : `= ${e.valor}`}
                                {e.teto_valor ? ` (teto ${e.teto_valor})` : ""}
                              </p>
                            ))}
                            {item.requisitos.map((r) => (
                              <p key={r.nome_variavel} className="text-xs text-muted-foreground font-mono">
                                requer {r.nome_variavel} {r.operador} {r.valor}
                              </p>
                            ))}
                            <p className="text-sm text-muted-foreground">
                              Custo: <span className="font-medium text-foreground">{item.custo} pts</span>
                              {" · "}Vendidas:{" "}
                              <span className="font-medium text-foreground">{item.vendidas}</span>
                              {item.limite_compras !== null && ` · Limite: ${item.limite_compras} por militar`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditLojaItem(item)}
                              className={greenOutlineButton}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteLojaItem(item)}
                              className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        )}
                      </ListaOrdenavel>
                    </div>
                  );
                })
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {aba === "niveis" && (
      <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
              <Award className="w-5 h-5" />
              Sistemas de nível
            </CardTitle>
            <CardDescription>
              Calcula o nível a partir de uma variável de XP (linear, exponencial ou manual) e paga recompensas por nível
            </CardDescription>
          </div>
          <Button
            onClick={() => {
              setEditingNivel(null);
              setIsNivelOpen(true);
            }}
            disabled={conexoes.length === 0}
            className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo sistema
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {niveis.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Nenhum sistema de nível cadastrado</div>
          ) : (
            <ListaOrdenavel itens={niveis} className="space-y-3" {...reordenacao("niveis", setNiveis)}>
              {(n) => (
              <div
                className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-foreground">{n.nome}</h3>
                    <AtivoBadge ativo={n.ativo} />
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    XP: {n.nome_variavel_xp} · {n.modo}
                    {n.modo !== "manual" && n.nivel_maximo ? ` · máx. nível ${n.nivel_maximo}` : ""}
                    {n.retroativo ? " · retroativo" : ""}
                  </p>
                  {n.recompensas.map((r, i) => (
                    <p key={i} className="text-xs text-muted-foreground font-mono">
                      {r.nome_variavel} += {r.valor} a cada {r.a_cada} nível(is), desde o {r.a_partir_de}
                      {r.teto_valor ? ` (teto ${r.teto_valor})` : ""}
                    </p>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingNivel(n);
                      setIsNivelOpen(true);
                    }}
                    className={greenOutlineButton}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteNivel(n)}
                    className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              )}
            </ListaOrdenavel>
          )}
        </CardContent>
      </Card>
      )}

      {aba === "missoes" && (
      <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
              <Swords className="w-5 h-5" />
              Missões
            </CardTitle>
            <CardDescription>
              Objetivos medidos por variáveis do jogo, com prazo, repetição e recompensas
            </CardDescription>
          </div>
          <Button
            onClick={() => {
              setEditingMissao(null);
              setIsMissaoOpen(true);
            }}
            disabled={conexoes.length === 0}
            className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova missão
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-2 p-3 border border-border rounded-lg flex-wrap">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="missoes_limite_global">
                Limite global de missões simultâneas por militar
              </label>
              <input
                id="missoes_limite_global"
                value={limiteGlobal}
                onChange={(e) => setLimiteGlobal(e.target.value)}
                inputMode="numeric"
                placeholder="Ilimitado"
                className="flex h-9 w-40 rounded-md border border-input bg-background px-3 py-1 text-sm font-mono"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleSalvarLimiteGlobal} className={greenOutlineButton}>
              Salvar limite
            </Button>
          </div>

          {missoes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Nenhuma missão cadastrada</div>
          ) : (
            <ListaOrdenavel itens={missoes} className="space-y-3" {...reordenacao("missoes", setMissoes)}>
              {(m) => (
              <div className="p-4 border border-border rounded-lg hover:bg-muted transition-colors space-y-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground">{m.nome}</h3>
                      <AtivoBadge ativo={m.ativo} />
                      {m.repetivel && <Badge variant="outline">Repetível</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {m.objetivos.length} objetivo(s)
                      {m.qtd_sorteio ? ` · sorteia ${m.qtd_sorteio}` : ""}
                      {m.prazo_tipo === "relativo" && m.prazo_horas ? ` · prazo de ${m.prazo_horas}h após aceitar` : ""}
                      {m.prazo_tipo === "fixo" && m.prazo_ate ? ` · até ${new Date(m.prazo_ate).toLocaleString("pt-BR")}` : ""}
                      {m.repetivel && m.cooldown_horas ? ` · intervalo de ${m.cooldown_horas}h` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleRegistros(m.id)}
                      className={greenOutlineButton}
                    >
                      <ScrollText className="w-4 h-4 mr-2" />
                      Registros
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingMissao(m);
                        setIsMissaoOpen(true);
                      }}
                      className={greenOutlineButton}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteMissao(m)}
                      className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {registrosAbertos === m.id && (
                  <div className="border-t border-border pt-3 space-y-1">
                    {registros === null ? (
                      <p className="text-xs text-muted-foreground">Carregando...</p>
                    ) : registros.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Nenhum registro ainda.</p>
                    ) : (
                      registros.map((r) => (
                        <p key={r.id} className="text-xs font-mono text-muted-foreground">
                          {r.militar ?? "?"} · {r.status.replace("_", " ")} · aceita em{" "}
                          {new Date(r.aceita_em).toLocaleString("pt-BR")}
                          {r.finalizada_em ? ` · finalizada em ${new Date(r.finalizada_em).toLocaleString("pt-BR")}` : ""}
                        </p>
                      ))
                    )}
                  </div>
                )}
              </div>
              )}
            </ListaOrdenavel>
          )}
        </CardContent>
      </Card>
      )}

      {aba === "estatisticas" && (
        <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
                <BarChart3 className="w-5 h-5" />
                Estatísticas
              </CardTitle>
              <CardDescription>
                Variáveis exibidas na aba Estatísticas do Sobrevivente, agrupadas por categoria
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                setEditingEstatistica(null);
                setIsEstatisticaOpen(true);
              }}
              disabled={conexoes.length === 0}
              className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova estatística
            </Button>
          </CardHeader>
          <CardContent>
            {estatisticas.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Nenhuma estatística cadastrada</div>
            ) : (
              <ListaOrdenavel
                itens={estatisticas}
                className="space-y-3"
                {...reordenacao("estatisticas", setEstatisticas)}
              >
                {(e) => (
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted transition-colors">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{e.rotulo}</h3>
                        <Badge variant="outline">{e.categoria}</Badge>
                        <AtivoBadge ativo={e.ativo} />
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">{e.nome_variavel}</p>
                      <p className="text-sm text-muted-foreground">
                        Conexão: <span className="font-medium text-foreground">{e.conexao_nome ?? "-"}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingEstatistica(e);
                          setIsEstatisticaOpen(true);
                        }}
                        className={greenOutlineButton}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteEstatistica(e)}
                        className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </ListaOrdenavel>
            )}
          </CardContent>
        </Card>
      )}

      <WiredEstatisticaModal
        isOpen={isEstatisticaOpen}
        onClose={() => {
          setIsEstatisticaOpen(false);
          setEditingEstatistica(null);
        }}
        estatistica={editingEstatistica}
        conexoes={conexoes}
        categorias={Array.from(new Set(estatisticas.map((e) => e.categoria)))}
        onSuccess={loadData}
      />

      <WiredMissaoModal
        isOpen={isMissaoOpen}
        onClose={() => {
          setIsMissaoOpen(false);
          setEditingMissao(null);
        }}
        missao={editingMissao}
        conexoes={conexoes}
        onSuccess={loadData}
      />

      <WiredNivelModal
        isOpen={isNivelOpen}
        onClose={() => {
          setIsNivelOpen(false);
          setEditingNivel(null);
        }}
        nivel={editingNivel}
        conexoes={conexoes}
        onSuccess={loadData}
      />

      <WiredLojaConfigModal
        isOpen={isLojaConfigOpen}
        onClose={() => setIsLojaConfigOpen(false)}
        config={editingLojaConfig}
        conexoes={conexoes}
        onSuccess={loadData}
      />

      <WiredLojaItemModal
        isOpen={isLojaItemOpen}
        onClose={() => {
          setIsLojaItemOpen(false);
          setEditingLojaItem(null);
        }}
        item={editingLojaItem}
        conexoes={conexoes}
        onSuccess={loadData}
      />

      <WiredHudAcaoModal
        isOpen={isAcaoModalOpen}
        onClose={handleCloseAcaoModal}
        acao={editingAcao}
        conexoes={conexoes}
        onSuccess={loadData}
      />

      <WiredHudVariavelModal
        isOpen={isHudModalOpen}
        onClose={handleCloseHudModal}
        variavel={editingHud}
        conexoes={conexoes}
        acoes={hudAcoes}
        onSuccess={loadData}
      />

      <WiredConexaoModal
        isOpen={isConexaoModalOpen}
        onClose={handleCloseConexaoModal}
        editingConexao={editingConexao}
        onSuccess={loadData}
      />

      <WiredFuncionalidadeModal
        isOpen={!!editingFuncionalidade}
        onClose={() => setEditingFuncionalidade(null)}
        funcionalidade={editingFuncionalidade}
        conexoes={conexoes}
        onSuccess={loadData}
      />

      <WiredTesteConexaoModal
        isOpen={!!testeResultado}
        onClose={handleCloseTeste}
        conexao={testeConexao}
        resultado={testeResultado}
      />
    </div>
  );
}
